<template>
  <b-tooltip
    ref="tooltip"
    :triggers="['click']"
    :auto-close="['outside', 'escape', 'inside']"
    :append-to-body="appendToBody"
    :class="{'no-append-to-body': !appendToBody}"
    position="is-bottom"
    type="is-white"
    class="tooltip-selector"
  >
    <slot v-bind:currentOption="currentOption" name="trigger">
      <TooltipSelectorOptionIcon :option="currentOption" class="selector-trigger" />
    </slot>
    <template v-slot:content>
      <div class="selector-content">
        <div
          v-for="(option, index) in options"
          :key="index"
          @click="updateValue(option)"
          :class="{'is-active': currentOption && currentOption.id === option.id, 'is-disabled': isOptionDisabled(option)}"
        >
          <TooltipSelectorOptionIcon :option="option" />
          <span class="is-small m-t-5">{{ option.label }}</span>
          <ProTagWithUpgradeTooltip
            v-if="isOptionDisabled(option)"
            :upgrade-intent="upgradeIntent"
            @upgrade="upgrade"
            class="tooltip-selector-pro-tag"
          />
        </div>
      </div>
    </template>
  </b-tooltip>
</template>

<script>
import TooltipSelectorOptionIcon from '@/components/decks/tooltip-selector/TooltipSelectorOptionIcon.vue'
import ProTagWithUpgradeTooltip from '@/components/upgrade/ProTagWithUpgradeTooltip.vue'

export default {
  name: 'TooltipSelector',
  components: { ProTagWithUpgradeTooltip, TooltipSelectorOptionIcon },
  props: {
    value: {
      type: String,
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    disableProOptions: {
      type: Boolean,
      default: false
    },
    upgradeIntent: {
      type: String,
      required: true
    },
    appendToBody: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      selected: this.$props.value
    }
  },
  computed: {
    currentOption () {
      return this.options.find(option => option.id === this.selected)
    }
  },
  watch: {
    value (val) {
      this.selected = val
    }
  },
  methods: {
    updateValue (option) {
      if (this.isOptionDisabled(option)) {
        return
      }
      this.$emit('input', option.id || null)
    },
    isOptionDisabled (option) {
      return option.isPro && this.disableProOptions
    },
    upgrade () {
      this.close()
      this.$emit('upgrade')
    },
    close () {
      // This is a bit hacky, but just using `.close()` will not work because of the `auto-close` prop.
      // @see node_modules/buefy/src/components/tooltip/Tooltip.vue
      this.$refs.tooltip.clickedOutside({ target: this.$el })
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/css/components';

$selector-border-radius: 6px;

.tooltip-selector {
  .selector-trigger {
    width: 40px;
    cursor: pointer;
  }

  .selector-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    background: $white;
    border-radius: $border-radius;

    > * {
      width: 6.5rem;
      padding: 1rem;
      justify-content: end;
      align-items: center;
      display: flex;
      flex-direction: column;
      cursor: pointer;
      border-right: 1px solid $tooltip-selector-inner-border-color;
      color: $tooltip-selector-color;
      position: relative;
      height: 95px;

      &:hover {
        background-color: $tooltip-selector-hover-background-color;
      }

      &:first-child {
        border-radius: $selector-border-radius 0 0 $selector-border-radius;
      }

      &:last-child {
        border-radius: 0 $selector-border-radius $selector-border-radius 0;
      }

      &.is-active {
        &:before {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          margin: 0 auto;
          bottom: 0;
          width: 70%;
          height: 4px;
          opacity: .5;
          border-radius: 10px;
          background-color: var(--secondary);
          z-index: 1;
        }

        &:hover {
          cursor: default;
        }
      }

      &.is-disabled {
        position: relative;
        cursor: not-allowed;

        > *:not(.tooltip-selector-pro-tag) {
          opacity: .5;
        }

        .tooltip-selector-pro-tag {
          position: absolute;
          top: 5px;
          right: 5px;
        }
      }
    }
  }

  .is-small {
    font-size: .75rem;
  }

  .tooltip-content {
    padding: 0;
  }

  &.b-tooltip.is-bottom.is-white {
    .tooltip-content {
      top: 15px;

      &::before {
        content: '';
        position: absolute;
        transform: rotate(45deg);
        width: 8px;
        height: 8px;
        top: -4px;
        left: calc(50% - 4px);
        z-index: -1;
        box-shadow: 1px 1px 5px 1px rgba(0, 0, 0, 0.2);
        background: $white;
      }
    }

    &.no-append-to-body .tooltip-content {
      top: 50px;
    }
  }
}
</style>
